import {
  Button,
  Group,
  Paper,
  Pill,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useMantineTheme,
  type TextareaProps,
  Box,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { isFileFormatValid } from "../utils/files";
import { FilePreview } from "./FilePreview";

interface Props extends TextareaProps {
  onFilesChange?: (files: File[]) => void;
  onFilesRemoved?: (files: File[]) => void;
  onFilesAdded?: (files: File[]) => void;
  onTextChange: (value: string) => void;
  text?: string;
  defaultText?: string;
  files: File[];
  acceptedTypes?: string[];
  error?: string;
  disabled?: boolean;
  filePreview?: boolean;
  bottomLeftSection?: React.ReactNode;
}

export const TextAreaWithDroppableFile = ({
  acceptedTypes,
  files,
  onFilesChange,
  onFilesAdded,
  onFilesRemoved,
  onTextChange,
  text,
  error,
  disabled,
  filePreview,
  defaultText,
  bottomLeftSection,
  ...textAreaProps
}: Props) => {
  const { t } = useTranslation("forms");
  const mantineTheme = useMantineTheme();

  const [isFileDragged, setIsFileDragged] = useState(false);
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isShaking, setIsShaking] = useState(false);

  const inputFileRef = useRef<HTMLInputElement>(null);

  const hasErrors = uploadErrors.length > 0 || !!error;

  const triggerShake = () => {
    setIsShaking(false);
    requestAnimationFrame(() => setIsShaking(true));
  };

  const isFileAlreadyUploaded = (file: File) =>
    files.some(({ name }) => name === file.name);

  const addFiles = (filesToAdd: FileList) => {
    const newErrors: string[] = [];

    Array.from(filesToAdd).forEach(file => {
      if (!isFileFormatValid({ file, acceptedTypes })) {
        newErrors.push(
          t(`components.textAreaWithDroppableFile.errors.invalidType`, {
            filename: file.name,
          })
        );
      }

      if (isFileAlreadyUploaded(file)) {
        newErrors.push(
          t(`components.textAreaWithDroppableFile.errors.alreadyExists`, {
            filename: file.name,
          })
        );
      }
    });

    if (newErrors.length) {
      addUploadErrors(newErrors);
      triggerShake();
      return;
    }

    clearUploadErrors();
    onFilesChange?.([...files, ...Array.from(filesToAdd)]);
    onFilesAdded?.(Array.from(filesToAdd));
  };

  const getBorderStyle = () => {
    if (isFileDragged) {
      return `2px dashed ${mantineTheme.colors.blue[3]}`;
    }

    if (hasErrors) {
      return `2px solid ${mantineTheme.colors.red[3]}`;
    }

    if (isTextAreaFocused) {
      return `2px solid ${mantineTheme.colors.blue[3]}`;
    }

    return `2px solid ${mantineTheme.colors.gray[3]}`;
  };

  const addUploadErrors = (errors: string[]) => {
    setUploadErrors(prevErrors => [...prevErrors, ...errors]);

    errors.forEach(errorMessage => {
      notifications.show({
        title: t("components.textAreaWithDroppableFile.errors.fileNotUploaded"),
        message: errorMessage,
        color: "red",
      });
    });
  };

  const clearUploadErrors = () => {
    setUploadErrors([]);
  };

  return (
    <Stack gap={0}>
      <Paper
        w="100%"
        radius="md"
        bd={getBorderStyle()}
        style={{
          animation: isShaking ? "textarea-shake 320ms ease-in-out" : undefined,
        }}
        bg={disabled ? "gray.1" : "transparent"}
        onAnimationEnd={() => setIsShaking(false)}
        onDragOver={e => {
          if (disabled) return;
          e.preventDefault();
          clearUploadErrors();
          setIsFileDragged(true);
        }}
        onDragLeave={e => {
          if (disabled) return;
          e.preventDefault();
          clearUploadErrors();
          setIsFileDragged(false);
        }}
        onDrop={e => {
          if (disabled) return;
          e.preventDefault();
          clearUploadErrors();
          setIsFileDragged(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <Textarea
          placeholder={t(
            "components.textAreaWithDroppableFile.placeholders.textArea"
          )}
          value={text}
          onChange={e => {
            clearUploadErrors();
            onTextChange(e.target.value);
          }}
          defaultValue={defaultText}
          {...textAreaProps}
          styles={{
            input: {
              border: "none",
              outline: "none",
              boxShadow: "none",
            },
          }}
          onFocus={e => {
            textAreaProps.onFocus?.(e);
            setIsTextAreaFocused(true);
          }}
          onBlur={e => {
            textAreaProps.onBlur?.(e);
            setIsTextAreaFocused(false);
          }}
          disabled={disabled}
        />
        <Group justify="space-between">
          {!disabled && (
            <Button
              size="xs"
              variant="subtle"
              onClick={() => {
                clearUploadErrors();
                inputFileRef.current?.click();
              }}
            >
              {t(
                "components.textAreaWithDroppableFile.labels.uploadFileButton"
              )}
            </Button>
          )}
          {bottomLeftSection && <Box px="xs">{bottomLeftSection}</Box>}
        </Group>
        <input
          accept={acceptedTypes?.join(",")}
          aria-label={t(
            `components.textAreaWithDroppableFile.labels.uploadFileButton`
          )}
          type="file"
          multiple
          ref={inputFileRef}
          style={{ display: "none" }}
          onChange={e => {
            if (e.target.files) {
              addFiles(e.target.files);
            }
          }}
        />
        {files.length > 0 && (
          // Prevents long file names from widening the parent layout, so pills get truncated instead
          <Group p="xs" style={{ contain: "inline-size" }}>
            {files.map(file => (
              <Tooltip
                label={filePreview ? <FilePreview file={file} /> : undefined}
                disabled={!filePreview}
                key={file.name}
              >
                <Pill
                  w="min-content"
                  withRemoveButton
                  onRemove={() => {
                    if (disabled) return;
                    const updatedFiles = files.filter(
                      f => f.name !== file.name
                    );
                    onFilesChange?.(updatedFiles);
                    onFilesRemoved?.([file]);
                  }}
                  c={disabled ? "gray" : undefined}
                >
                  {file.name}
                </Pill>
              </Tooltip>
            ))}
          </Group>
        )}

        <style>
          {`@keyframes textarea-shake {
          0% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
          100% { transform: translateX(0); }
        }`}
        </style>
      </Paper>
      {error && (
        <Stack align="flex-end">
          <Text size="xs" c="red" mt="xs">
            {error}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
